import { Activity, useEffect, useState } from 'react'
import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import PasswordField from '../../components/common/PasswordField'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { useNavigate } from 'react-router-dom'

const CreateUserPage = () => {
  const [userData, setUserData] = useState({
    email: '',
    password: '',
    profile: {
      firstName: '',
      lastName: '',
    },
    organization: '',
    role: '',
  })
  const [isPasswordConfirmed, setIsPasswordConfirmed] = useState(false)
  const [organizations, setOrganizations] = useState([])
  const [roles, setRoles] = useState([])
  const [errors, setErrors] = useState({})
  const navigate = useNavigate()

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [orgResponse, rolesResponse] = await Promise.all([
          fetchWithAuth('organizations'),
          fetchWithAuth('roles'),
        ])
        setOrganizations(orgResponse.data.data)
        setRoles(rolesResponse.data.data)
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }

    fetchData()
  })

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? ''
          : 'Please enter a valid email address'
      case 'password':
        return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(value)
          ? ''
          : 'Password must be at least 8 characters, including uppercase, lowercase, and numbers'
      case 'firstName':
      case 'lastName':
        return value.length >= 2 && value.length <= 35
          ? ''
          : 'Must be between 2 and 35 characters'
      case 'organization':
        return value ? '' : 'Organization must be selected'
      case 'role':
        return value ? '' : 'Role must be selected'
      default:
        return ''
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    let updatedUserData

    if (name === 'firstName' || name === 'lastName') {
      updatedUserData = {
        ...userData,
        profile: { ...userData.profile, [name]: value },
      }
    } else {
      updatedUserData = { ...userData, [name]: value }
    }

    setUserData(updatedUserData)

    const error = validateField(name, value)
    setErrors((prev) => ({ ...prev, [name]: error }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const newErrors = {}
    Object.keys(userData).forEach((key) => {
      if (key === 'profile') {
        newErrors.firstName = validateField(
          'firstName',
          userData.profile.firstName,
        )
        newErrors.lastName = validateField(
          'lastName',
          userData.profile.lastName,
        )
      } else {
        newErrors[key] = validateField(key, userData[key])
      }
    })

    setErrors(newErrors)

    const isValid = Object.values(newErrors).every((error) => error === '')

    if (isValid && isPasswordConfirmed) {
      try {
        const { status } = await fetchWithAuth('users', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userData),
        })

        if (status !== 201) {
          throw new Error(`HTTP error! status: ${status}`)
        }
        navigate('/users')
      } catch (error) {
        console.error('Error creating user:', error)
      }
    }
  }

  return (
    <Paper sx={{ p: 3, mx: 'auto', maxWidth: '600px' }}>
      <Typography variant="h6" component="h1" gutterBottom>
        Create New User
      </Typography>
      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            label="Email"
            name="email"
            type="email"
            value={userData.email}
            onChange={handleChange}
            fullWidth
            required
            error={!!errors.email}
            helperText={errors.email}
          />
          <PasswordField
            value={userData.password}
            onChange={handleChange}
            error={!!errors.password}
            helperText={errors.password}
            fullWidth
            required
            confirmation={{
              label: 'Confirm Password',
              onMatchChange: setIsPasswordConfirmed,
            }}
          />
          <TextField
            label="First Name"
            name="firstName"
            value={userData.profile.firstName}
            onChange={handleChange}
            fullWidth
            error={!!errors.firstName}
            helperText={errors.firstName}
          />
          <TextField
            label="Last Name"
            name="lastName"
            value={userData.profile.lastName}
            onChange={handleChange}
            fullWidth
            error={!!errors.lastName}
            helperText={errors.lastName}
          />
          <FormControl fullWidth error={!!errors.organization}>
            <InputLabel id="organization-label" htmlFor="organization">
              Organization
            </InputLabel>
            <Select
              labelId="organization-label"
              name="organization"
              value={userData.organization}
              label="Organization"
              inputProps={{ id: 'organization' }}
              onChange={handleChange}
              required
            >
              {organizations.map((org) => (
                <MenuItem key={org.uuid} value={org.uuid}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
            <Activity mode={errors.organization ? 'visible' : 'hidden'}>
              <FormHelperText>{errors.organization}</FormHelperText>
            </Activity>
          </FormControl>
          <FormControl fullWidth error={!!errors.role}>
            <InputLabel id="role-label" htmlFor="role">
              Role
            </InputLabel>
            <Select
              labelId="role-label"
              name="role"
              value={userData.role}
              label="Role"
              inputProps={{ id: 'role' }}
              onChange={handleChange}
              required
            >
              {roles &&
                roles.map((role) => (
                  <MenuItem key={role.name} value={role.name}>
                    {role.name
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                  </MenuItem>
                ))}
            </Select>
            <Activity mode={errors.role ? 'visible' : 'hidden'}>
              <FormHelperText>{errors.role}</FormHelperText>
            </Activity>
          </FormControl>
          <Button variant="contained" color="primary" type="submit">
            Create User
          </Button>
        </Stack>
      </form>
    </Paper>
  )
}

export default CreateUserPage
